import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ProductMetrics, ProductSort } from "@unbox/api";
import { FilterLines, ShoppingBag01 } from "@untitledui/icons";
import { SearchMd } from "@untitledui/icons/SearchMd";
import { useMemo, useState } from "react";
import { usePagination } from "@/api/pagination";
import { useProductMetricsDownload, useProductsCount, useProductsMetrics } from "@/api/product-metrics";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { ExportCsvButton } from "@/components/ExportCsvButton";
import { FilterPills } from "@/components/filters/filter-pills";
import { ProductFilterPanel } from "@/components/filters/panels";
import { LoadingPageState } from "@/components/loading-page-state";
import { PageLayout } from "@/components/page-layout";
import { generateFilterSyncConfig, useAutoFilterUrlSync } from "@/hooks/use-auto-filter-sync";
import { useCache } from "@/hooks/use-cache";
import { useDashboardData } from "@/hooks/use-dashboard-data";
import { useDateRangeUrlSync } from "@/hooks/use-date-range-url-sync";
import { useClearableDebounce } from "@/hooks/use-debounce";
import { useExplorerDateFilter } from "@/hooks/use-explorer-date-filter";
import { useSearchUrlSync } from "@/hooks/use-search-url-sync";
import { useSort } from "@/hooks/use-sort";
import { useUrlFilterSync } from "@/hooks/use-url-filter-sync";
import { ProductExplorerTable } from "@/pages/explorers/product-explorer-table";
import { Metrics } from "@/pages/explorers/utils";
import { ProductDetails } from "@/pages/products/product-details";
import { FiltersProvider, useFilters } from "@/providers/filter-provider";
import type { productExplorerRoute } from "@/router";
import { cx } from "@/utils/cx";
import { downloadCSV } from "@/utils/download-csv";
import { AllProductsEmptyState } from "./all-products-empty-state";

const transformProductData = (data: { products: ProductMetrics[] }) => {
  return data.products.map((product) => ({
    ...product,
    videosChange: product.trendVideos ? (product.videos - product.trendVideos) / product.trendVideos : null,
    unitSalesChange: product.trendUnitSales
      ? (product.unitSales - product.trendUnitSales) / product.trendUnitSales
      : null,
    gmvChange: product.trendGmv ? (product.gmv - product.trendGmv) / product.trendGmv : null,
    price: [...new Set(product.price)],
  }));
};

export type ProductRow = ReturnType<typeof transformProductData>[number];

const ProductExplorerContent = () => {
  const urlParams = useSearch({ strict: false }) as ReturnType<(typeof productExplorerRoute)["useSearch"]>;
  const { product_id, startDate, endDate, search: urlSearch } = urlParams;
  const navigate = useNavigate() as ReturnType<(typeof productExplorerRoute)["useNavigate"]>;
  const [selectedProductId, setSelectedProductId] = useState<string | undefined>(product_id);
  const { dateRange, isApplied, clear: clearDateRange, changeDate } = useExplorerDateFilter();

  // Sync date range to URL
  useDateRangeUrlSync({
    dateRange,
    changeDate,
    navigate,
    currentSearch: { startDate, endDate },
    isApplied,
  });

  const [search, setSearch] = useState(urlSearch || "");
  const { value: debouncedSearch, clear: clearSearch } = useClearableDebounce(search);

  // Sync search to URL
  useSearchUrlSync({
    search,
    setSearch,
    navigate,
    currentSearch: { search: urlSearch },
  });
  const pagination = usePagination();
  const { sort, setFromDescriptor, descriptor } = useSort<ProductSort>("gmv");
  const { form, apiFilters, reset, activeFields, hasActiveFilters: hasActiveFilterFields } = useFilters();

  // Automatically sync URL params to form based on active fields
  useAutoFilterUrlSync(form, urlParams, activeFields);

  // Generate field mapping and currentSearch dynamically
  const { fieldMapping, currentSearch } = useMemo(
    () => generateFilterSyncConfig(activeFields, urlParams),
    [activeFields, urlParams],
  );

  // Sync form changes back to URL
  useUrlFilterSync({
    form,
    fieldMapping,
    navigate,
    currentSearch,
  });
  const filters = useMemo(
    () => ({
      ...apiFilters,
      text: debouncedSearch,
    }),
    [apiFilters, debouncedSearch],
  );

  const [filtersOpen, setFiltersOpen] = useState(false);

  const { data: overview } = useDashboardData();
  const { data } = useProductsMetrics({ range: dateRange, pagination: pagination.pagination, sort, ...filters });
  const { data: count, isLoading: isLoadingCount } = useProductsCount({ range: dateRange, ...filters });
  const total = useCache(count?.totalProducts);

  const tableData = useMemo(() => {
    if (!data) return undefined;
    return transformProductData(data);
  }, [data]);

  const { mutateAsync, isPending: isDownloading } = useProductMetricsDownload();
  const downloadTableDate = async () => {
    if (!total) return;
    const rows = await mutateAsync({
      total,
      range: dateRange,
      sort,
      ...filters,
    });

    downloadCSV(
      rows.map((r) => ({ ...r, productId: r.productId.replace("PID-", "") })),
      "product_explorer",
      ["productId", "productName", "gmv", "unitSales", "videos", "stockCount"],
      ["Product ID", "Product Name", "GMV", "Unit Sales", "Videos", "Stock"],
    );
  };

  const resetFilters = () => {
    setSearch("");
    clearSearch();
    clearDateRange();
    reset();
  };

  // Genuine zero-data (no products, no filters, search or custom range) replaces
  // the whole page body. A filtered/searched empty keeps the inline table state.
  const isUnfiltered = !hasActiveFilterFields && !debouncedSearch && !isApplied;
  if (isUnfiltered && isLoadingCount && total === undefined) return <LoadingPageState />;

  if (total === 0 && isUnfiltered) {
    return (
      <AllProductsEmptyState
        onAddProducts={() => window.open("https://seller.tiktok.com", "_blank", "noopener,noreferrer")}
      />
    );
  }

  return (
    <PageLayout
      drawerOpen={filtersOpen}
      title="Products"
      icon={ShoppingBag01}
      subtitle="See how your products are performing"
      callToAction={<ExportCsvButton onClick={downloadTableDate} isDownloading={isDownloading} label="Export as CSV" />}
    >
      <div className="flex flex-col gap-5 flex-1 min-h-0 w-full">
        <div className="flex flex-row gap-4 w-full flex-shrink-0">
          <Metrics data={overview?.revenueKPIs.weeklyAOV} />
          <Metrics data={overview?.revenueKPIs.unitSalesRevenue} />
          <Metrics data={overview?.mainKPIs.weeklyGMV} />
        </div>
        <div className="flex flex-row items-center justify-between flex-shrink-0">
          <span className="text-sm font-medium">{count ? `${count.totalProducts} Products` : "Loading..."}</span>
          <div className="flex items-center flex-row gap-2">
            <Input
              size="sm"
              inputClassName="text-sm"
              className="w-96"
              value={search}
              onChange={setSearch}
              placeholder="Search Product name..."
              icon={SearchMd}
            />
            <Button
              className="font-medium text-xs"
              color="secondary"
              onClick={() => setFiltersOpen((prev) => !prev)}
              iconLeading={<FilterLines className="w-4 h-4" />}
            >
              Filter
            </Button>
          </div>
        </div>

        <FilterPills className="-my-2" />
        <ProductExplorerTable
          rows={tableData}
          dateRange={dateRange}
          onClickRow={(row) => {
            setSelectedProductId(row.productId);
            navigate({ search: (prev) => ({ ...prev, product_id: row.productId }) });
          }}
          sorting={{ descriptor, onSortChange: setFromDescriptor }}
          pagination={{
            page: pagination.page,
            totalPages: Math.ceil((total ?? 0) / pagination.pagination.limit),
            onPageChange: pagination.setPage,
          }}
          hasActiveFilters={hasActiveFilterFields || !!debouncedSearch || isApplied}
          onClearFilters={resetFilters}
        />

        <ProductDetails
          id={selectedProductId}
          tab={urlParams.tab}
          onTabChange={(tab) => navigate({ search: (prev) => ({ ...prev, tab }) })}
          onClose={() => {
            setSelectedProductId(undefined);
            navigate({ search: (prev) => ({ ...prev, product_id: undefined, tab: undefined }) });
          }}
        />
      </div>

      <ProductFilterPanel
        open={filtersOpen}
        close={() => setFiltersOpen(false)}
        clearAll={() => resetFilters()}
        dateRange={dateRange}
      />
    </PageLayout>
  );
};
const ACTIVE_FIELDS = [
  // Top-level filters
  "minGmv",
  "maxGmv",
  "stockStatus",
  // Performance section
  "minUnitsSold",
  "maxUnitsSold",
  "minVideos",
  "maxVideos",
  "minAov",
  "maxAov",
  // Product Details section
  "minPrice",
  "maxPrice",
  "minStockLevel",
  "maxStockLevel",
  "category",
] as const;

export const ProductExplorer = () => {
  const urlParams = useSearch({ strict: false }) as ReturnType<(typeof productExplorerRoute)["useSearch"]>;

  return (
    <FiltersProvider activeFields={[...ACTIVE_FIELDS]} initialValues={urlParams}>
      <ProductExplorerContent />
    </FiltersProvider>
  );
};
